import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import {
  reconnectNumberPerDaySelector,
  updateReconnectNumberPerDay,
} from '@repo/redux/actions/userSettings';
import { Range } from '@components/FormInputs';
import styles from './Reconnect.module.scss';
import homeStyles from '../Home.module.scss';
import { SettingToggle, SettingsSectionRow } from '@views/Settings/components';
import useEnabledNotifications from '../../Notifications/use-enabled-notifications';
import { fetchActivity } from '@repo/redux/actions/activity';

function ReconnectCount({ disabled }: { disabled: boolean }) {
  const dispatch = useDispatch();
  const savedCount = useSelector(reconnectNumberPerDaySelector);
  const [selectedCount, setSelectedCount] = useState(savedCount);
  const saveSelectedCount = (numberPerDay: number) => {
    dispatch(updateReconnectNumberPerDay({ numberPerDay }));
  };

  return (
    <div className={cn(styles.container, disabled && styles.disabled)}>
      Number of Automatic Reconnects
      <span className={styles.slider}>
        <span>{selectedCount}</span>
        <Range
          key={`${savedCount}_${disabled}`}
          defaultValue={disabled ? 0 : savedCount}
          onChange={setSelectedCount}
          onDebouncedChange={saveSelectedCount}
          max={10}
          disabled={disabled}
        />
      </span>
      <p>
        Clay can intelligently recommend relationships we think you should be mindful of. Select how
        many people you'd like Clay to suggest daily—we recommend starting with 3. Change this to 0
        to only receive Reconnects for people for whom you've set a specific cadence (i.e. monthly,
        quarterly, etc.).
      </p>
    </div>
  );
}

function ReconnectGrouping({ disabled }: { disabled: boolean }) {
  const dispatch = useDispatch();
  const { enabledNotifications, toggleNotification, updatingEnabledNotifications } =
    useEnabledNotifications();
  const [reconnectsGrouped, setReconnectsGrouped] = useState(
    enabledNotifications['activity_group_reconnects'],
  );

  useEffect(() => {
    if (
      reconnectsGrouped !== enabledNotifications['activity_group_reconnects'] &&
      !updatingEnabledNotifications
    ) {
      dispatch(fetchActivity());
      setReconnectsGrouped(enabledNotifications['activity_group_reconnects']);
    }
  }, [reconnectsGrouped, updatingEnabledNotifications]);

  return (
    <SettingsSectionRow className={cn(homeStyles.container, disabled && homeStyles.disabled)}>
      <SettingToggle
        title="Grouping by day"
        checked={disabled ? false : enabledNotifications['activity_group_reconnects']}
        name="activity_group_reconnects"
        onChange={() => toggleNotification('activity_group_reconnects')}
        disabled={disabled}
      />
      <p>
        Show all Reconnect recommendations for each day as a single item. When disabled, each person
        will appear as a separate Home feed item.
      </p>
    </SettingsSectionRow>
  );
}

export default function Reconnect({ disabled }: { disabled: boolean }) {
  return (
    <>
      <ReconnectCount disabled={disabled} />
      <ReconnectGrouping disabled={disabled} />
    </>
  );
}
